import { useRef, useState, type KeyboardEvent } from 'react';
import { Paperclip, Send } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ComposerProps {
  onSend: (text: string) => void;
  disabled?: boolean;
  sending?: boolean;
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  picker?: {
    accept: string;
    disabled: boolean;
    busy: boolean;
    onPick: (files: File[]) => void;
  };
}

export function Composer({
  onSend,
  disabled,
  sending,
  placeholder = 'Message support…',
  value: controlledValue,
  onValueChange,
  picker,
}: ComposerProps) {
  const [localValue, setLocalValue] = useState('');
  const value = controlledValue ?? localValue;
  const setValue = onValueChange ?? setLocalValue;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const canSend = value.trim().length > 0 && !disabled && !sending && !picker?.busy;

  function submit() {
    const trimmed = value.trim();
    if (!trimmed || picker?.busy) return;
    onSend(trimmed);
    setValue('');
    // Reset height
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function onInput() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }

  return (
    <div className="flex items-end gap-0 border-t border-line-subtle bg-page">
      {picker && (
        <>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={picker.accept}
            tabIndex={-1}
            aria-hidden
            className="sr-only"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length) picker.onPick(files);
            }}
          />
          <button
            type="button"
            aria-label="Add photos"
            onClick={() => fileRef.current?.click()}
            disabled={disabled || sending || picker.disabled}
            className={cn(
              'shrink-0 w-48 h-48 flex items-center justify-center',
              'text-ink-muted hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed',
            )}
          >
            <Paperclip size={18} strokeWidth={1.5} />
          </button>
        </>
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        onInput={onInput}
        placeholder={placeholder}
        disabled={disabled || sending}
        rows={1}
        className={cn(
          'flex-1 resize-none bg-transparent px-15 py-14 outline-none',
          // font-size MUST be >= 16px to prevent iOS auto-zoom on focus
          'text-16 leading-140 text-ink placeholder:text-ink-disabled',
          'disabled:opacity-50',
        )}
        style={{ minHeight: 48, maxHeight: 120 }}
      />
      <button
        type="button"
        aria-label="Send message"
        onClick={submit}
        disabled={!canSend}
        className={cn(
          'shrink-0 w-48 h-48 flex items-center justify-center',
          'text-ink-muted',
          canSend ? 'hover:text-ink' : 'opacity-30 cursor-not-allowed',
        )}
      >
        {sending ? (
          <svg width={16} height={16} viewBox="0 0 24 24" className="animate-spin" aria-hidden>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25" />
            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
          </svg>
        ) : (
          <Send size={18} strokeWidth={1.5} />
        )}
      </button>
    </div>
  );
}
